import React from "react";
import { BRAND } from "../lib/brand";

export const COMPANY = {
  name: "SNEHA FOODS",
  brand: "EATH'S",
  address_lines: ["Plot No 56/A, F Block, Autonagar", "Visakhapatnam, Andhra Pradesh 530012"],
  phone: "+91 78933 04411",
  email: "founder@snehafoods.in",
  website: "www.snehafoods.in",
  gstin: "37ABFPE7164F1ZQ",
  fssai: "10126003000320",
  founder: "E. Sneha Latha",
};

// Palette from letterhead
export const LH_ORANGE = "#d47a4b";
export const LH_GREEN = "#2b5c34";
export const LH_CREAM = "#f5f0e1";

/**
 * A reusable letterhead-style wrapper for printable documents:
 * payslip, sales invoice, purchase order.
 *
 * Usage:
 *   <Letterhead docTitle="SALES INVOICE" docMeta={[["Invoice #", "INV-001"], ["Date", "2025-08-01"]]}>
 *     ...document body...
 *   </Letterhead>
 */
export default function Letterhead({ docTitle, docMeta = [], children, footer, pageHead }) {
  return (
    <div
      className="relative bg-white text-zinc-900 letterhead-a4 mx-auto flex flex-col"
      style={{
        borderLeft: `6px solid ${LH_ORANGE}`,
        width: "210mm",
        minHeight: "297mm",
        boxSizing: "border-box",
        boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
      }}
      data-testid="letterhead"
    >
      {/* Header — repeated on every printed page via @media print rules */}
      <div className="lh-header px-8 pt-8 pb-4">
        <div className="flex items-start justify-between gap-8">
          <div className="flex items-start gap-4 min-w-0">
            <img src={BRAND.logo} alt="EATH's" className="w-16 h-16 object-contain shrink-0" />
            <div className="min-w-0">
              <div className="text-2xl font-black tracking-tight" style={{ color: LH_ORANGE }}>{COMPANY.name}</div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">Manufacturers of <span className="brand-font text-sm normal-case tracking-normal" style={{ color: LH_ORANGE }}>{COMPANY.brand}</span></div>
              <div className="text-[11px] text-zinc-700 mt-2 font-mono">GSTIN: {COMPANY.gstin}</div>
              <div className="text-[11px] text-zinc-700 font-mono">FSSAI: {COMPANY.fssai}</div>
            </div>
          </div>
          <div className="text-right text-xs text-zinc-700 leading-relaxed shrink-0">
            {COMPANY.address_lines.map((l, i) => <div key={i}>{l}</div>)}
            <div className="mt-2 font-mono">📞 {COMPANY.phone}</div>
            <div className="font-mono">✉ {COMPANY.email}</div>
          </div>
        </div>
        {/* Orange separator */}
        <div style={{ height: 2, background: LH_ORANGE }} className="mt-4" />

        {/* Title bar — INSIDE lh-header so it repeats on every printed page */}
        {(docTitle || docMeta.length > 0) && (
          <div className="lh-title-bar pt-4 flex flex-wrap items-end justify-between gap-6">
            {docTitle && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Document</div>
                <div className="text-3xl font-black tracking-tight">{docTitle}</div>
              </div>
            )}
            {docMeta.length > 0 && (
              <table className="text-sm">
                <tbody>
                  {docMeta.map((row, i) => (
                    <tr key={i}>
                      <td className="pr-3 py-0.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 text-right">{row[0]}</td>
                      <td className="font-bold">{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Optional per-doc "page head" slot (e.g. Billed To + Reference).
            Rendered INSIDE lh-header so it repeats at the top of every
            printed page along with the brand strip and title bar. */}
        {pageHead && (
          <div className="lh-page-head pt-5">
            {pageHead}
          </div>
        )}
      </div>

      {/* Watermark SF logo — centred on the page. On screen it stays inside
          the letterhead frame; in print, printDom.js re-anchors it with
          position:fixed so it repeats on every page. */}
      <div className="lh-watermark pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden" style={{ zIndex: 0 }}>
        <img src="/logo/sf-logo.webp" alt="" className="select-none" style={{ width: 420, height: 420, opacity: 0.09, maxWidth: "55%" }} />
      </div>

      {/* Body */}
      <div className="lh-body relative px-8 pb-8 pt-6 flex-1" style={{ zIndex: 1 }}>
        {children}
      </div>

      {/* Footer — repeated on every printed page via @media print rules */}
      <div className="lh-footer relative px-8 py-4 text-white text-center print:!bg-emerald-700" style={{ background: LH_GREEN, zIndex: 1, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
        <div className="font-black tracking-widest text-sm">{COMPANY.name}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-80 mt-1">
          {COMPANY.founder} · Founder · {COMPANY.website}
        </div>
        {footer && <div className="mt-2 text-[11px] opacity-90">{footer}</div>}
      </div>
    </div>
  );
}
